import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  Check,
  LogOut,
  Shield,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "sa_admin_token";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatusBadge = ({ status }) => {
  const map = {
    pending_verification: {
      label: "Pending",
      cls: "bg-[#DCD7C9] text-[#0A1E3F] border-[#0A1E3F]/15",
      Icon: Clock,
    },
    verified: {
      label: "Verified",
      cls: "bg-[#0A1E3F] text-[#D4A53A] border-[#0A1E3F]",
      Icon: CheckCircle2,
    },
    rejected: {
      label: "Rejected",
      cls: "bg-[#C65D47]/10 text-[#C65D47] border-[#C65D47]/30",
      Icon: XCircle,
    },
  };
  const s = map[status] || map.pending_verification;
  return (
    <span
      data-testid={`status-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono-display text-[10px] uppercase tracking-[0.16em] ${s.cls}`}
    >
      <s.Icon size={11} />
      {s.label}
    </span>
  );
};

const TokenGate = ({ onSubmit, error }) => {
  const [token, setToken] = useState("");
  return (
    <div className="min-h-screen bg-[#0A1E3F] text-[#F4F1EA] flex items-center justify-center px-6 paper-grain">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(token.trim());
        }}
        className="w-full max-w-md bg-[#F4F1EA] text-[#1C1A17] border border-[#DCD7C9] rounded-lg p-7"
      >
        <div className="flex items-center gap-2 text-[#D4A53A]">
          <Shield size={16} />
          <span className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B]">
            Admin · The Story Assembly
          </span>
        </div>
        <h1 className="font-serif text-3xl tracking-tighter mt-2 text-[#0A1E3F]">
          Sign in with your admin token
        </h1>
        <p className="mt-3 text-sm text-[#1C1A17]/70 leading-relaxed">
          Paste the <code className="font-mono-display text-[#0A1E3F]">ADMIN_TOKEN</code> from your
          backend env. The token is kept in your browser's local storage and
          sent only as the <code className="font-mono-display text-[#0A1E3F]">X-Admin-Token</code> header.
        </p>
        <label className="block mt-6">
          <span className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B]">
            Admin token
          </span>
          <input
            data-testid="admin-token-input"
            type="password"
            autoFocus
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="paste your token"
            className="mt-2 w-full bg-transparent border-b border-[#DCD7C9] focus:border-[#0A1E3F] focus:outline-none py-2 font-mono-display"
          />
        </label>
        {error && (
          <p data-testid="admin-token-error" className="mt-3 text-sm text-[#C65D47]">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-token-submit"
          className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 bg-[#0A1E3F] text-[#F4F1EA] text-sm font-medium hover:bg-[#061226] transition-all active:scale-[0.97]"
        >
          Unlock dashboard
        </button>
      </form>
    </div>
  );
};

const Row = ({ row, token, onChanged }) => {
  const [copied, setCopied] = useState(false);
  const [updating, setUpdating] = useState(null); // 'verified' | 'rejected' | null
  const copy = async () => {
    await navigator.clipboard.writeText(row.upi_ref_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  const update = async (status) => {
    setUpdating(status);
    try {
      await axios.patch(
        `${API}/payment-confirmations/${row.id}`,
        { status },
        { headers: { "X-Admin-Token": token } }
      );
      onChanged();
    } catch {
      // intentionally swallowed — admin sees the row's stale status until next refresh
    } finally {
      setUpdating(null);
    }
  };
  return (
    <tr
      data-testid={`admin-row-${row.id}`}
      className="border-t border-[#DCD7C9] hover:bg-[#E6E2D6]/40 transition-colors"
    >
      <td className="px-4 py-4">
        <div className="font-medium text-[#0A1E3F]">{row.name}</div>
        <a
          href={`mailto:${row.email}`}
          className="text-[13px] text-[#1C1A17]/70 hover:text-[#D4A53A]"
        >
          {row.email}
        </a>
      </td>
      <td className="px-4 py-4">
        <span
          className={`font-mono-display text-[11px] uppercase tracking-[0.18em] rounded-full px-2 py-1 ${
            row.plan === "pro"
              ? "bg-[#0A1E3F] text-[#D4A53A]"
              : "bg-[#D4A53A]/20 text-[#0A1E3F]"
          }`}
        >
          {row.plan}
        </span>
      </td>
      <td className="px-4 py-4 font-serif text-xl text-[#0A1E3F]">{fmtINR(row.amount)}</td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <code className="font-mono-display text-[13px] text-[#0A1E3F]">
            {row.upi_ref_id}
          </code>
          <button onClick={copy} className="text-[#1C1A17]/50 hover:text-[#D4A53A]">
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </div>
      </td>
      <td className="px-4 py-4 text-[12px] font-mono-display text-[#6B655B]">
        {fmtDate(row.created_at)}
      </td>
      <td className="px-4 py-4">
        <StatusBadge status={row.status} />
      </td>
      <td className="px-4 py-4">
        {row.status === "pending_verification" ? (
          <div className="flex gap-2">
            <button
              data-testid={`row-verify-${row.id}`}
              disabled={updating !== null}
              onClick={() => update("verified")}
              className="inline-flex items-center gap-1 rounded-full bg-[#0A1E3F] text-[#F4F1EA] px-3 py-1.5 text-[12px] hover:bg-[#061226] disabled:opacity-50"
            >
              {updating === "verified" ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <CheckCircle2 size={11} />
              )}
              Verify
            </button>
            <button
              data-testid={`row-reject-${row.id}`}
              disabled={updating !== null}
              onClick={() => update("rejected")}
              className="inline-flex items-center gap-1 rounded-full border border-[#C65D47]/40 text-[#C65D47] px-3 py-1.5 text-[12px] hover:bg-[#C65D47]/10 disabled:opacity-50"
            >
              {updating === "rejected" ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <XCircle size={11} />
              )}
              Reject
            </button>
          </div>
        ) : (
          <button
            data-testid={`row-reset-${row.id}`}
            onClick={() => update("pending_verification")}
            disabled={updating !== null}
            className="inline-flex items-center gap-1 rounded-full border border-[#DCD7C9] px-3 py-1.5 text-[12px] text-[#1C1A17]/70 hover:border-[#0A1E3F]/40 disabled:opacity-50"
          >
            Reset to pending
          </button>
        )}
      </td>
    </tr>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authError, setAuthError] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState([]);

  const load = useCallback(
    async (t) => {
      const useToken = t || token;
      if (!useToken) return;
      setLoading(true);
      setError("");
      try {
        const [confs, ana, lds] = await Promise.all([
          axios.get(`${API}/payment-confirmations?limit=200`, {
            headers: { "X-Admin-Token": useToken },
          }),
          axios
            .get(`${API}/analytics`, {
              headers: { "X-Admin-Token": useToken },
            })
            .catch(() => ({ data: null })),
          axios
            .get(`${API}/admin/lead-magnet-subscribers?limit=200`, {
              headers: { "X-Admin-Token": useToken },
            })
            .catch(() => ({ data: { items: [] } })),
        ]);
        setItems(confs.data.items || []);
        setAnalytics(ana.data);
        setLeads(lds.data?.items || []);
        setAuthed(true);
        setAuthError("");
        localStorage.setItem(TOKEN_KEY, useToken);
      } catch (err) {
        if (err?.response?.status === 401) {
          setAuthed(false);
          setAuthError("Invalid token. Check your backend ADMIN_TOKEN.");
          localStorage.removeItem(TOKEN_KEY);
        } else if (err?.response?.status === 503) {
          setAuthError(
            "Admin listing is disabled on the backend (ADMIN_TOKEN env var not set)."
          );
        } else {
          setError("Could not load payment confirmations.");
        }
      } finally {
        setLoading(false);
      }
    },
    // axios + API + TOKEN_KEY are stable module references; setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]
  );

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuth = (val) => {
    setToken(val);
    setAuthError("");
    load(val);
  };
  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setItems([]);
  };

  if (!authed) return <TokenGate onSubmit={handleAuth} error={authError} />;

  const filtered =
    filter === "all" ? items : items.filter((r) => r.status === filter);
  const stats = items.reduce(
    (acc, r) => {
      acc.total += 1;
      acc[r.status] = (acc[r.status] || 0) + 1;
      acc.revenue +=
        r.status === "verified" ? Number(r.amount || 0) : 0;
      return acc;
    },
    { total: 0, revenue: 0 }
  );

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#1C1A17]">
      <header className="border-b border-[#DCD7C9] bg-[#F4F1EA]/90 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B]">
              Admin · The Story Assembly
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={() => load()}
              className="inline-flex items-center gap-1.5 text-sm text-[#0A1E3F] hover:text-[#D4A53A]"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              data-testid="admin-signout"
              onClick={signOut}
              className="inline-flex items-center gap-1.5 text-sm text-[#0A1E3F]/70 hover:text-[#C65D47]"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Site Analytics */}
        {analytics && (
          <section data-testid="admin-analytics" className="mb-12">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-[#0A1E3F]">
                  Site analytics
                </h2>
                <p className="text-[#1C1A17]/70 mt-1 text-[15px]">
                  Unique visitors, demo usage, and the funnel to revenue.
                </p>
              </div>
            </div>

            {/* Headline tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              {[
                {
                  label: "Visitors today",
                  value: analytics.visitors.today,
                  sub: `${analytics.visitors.yesterday} yesterday`,
                  testid: "ana-visitors-today",
                },
                {
                  label: "Visitors · last 7d",
                  value: analytics.visitors.week,
                  sub: `${analytics.visitors.total} all-time`,
                  testid: "ana-visitors-week",
                },
                {
                  label: "Demo users today",
                  value: analytics.demo.today_users,
                  sub: `${analytics.demo.total_generations} generations`,
                  testid: "ana-demo-today",
                },
                {
                  label: "Verified revenue",
                  value: fmtINR(analytics.payments.verified_revenue),
                  sub: `${analytics.payments.verified} verified`,
                  testid: "ana-revenue",
                },
              ].map((t) => (
                <div
                  key={t.label}
                  data-testid={t.testid}
                  className="border border-[#DCD7C9] rounded-md p-5 bg-[#F4F1EA]"
                >
                  <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                    {t.label}
                  </div>
                  <div className="font-serif text-3xl md:text-4xl tracking-tighter mt-2 text-[#0A1E3F]">
                    {t.value}
                  </div>
                  <div className="mt-1 text-[12px] text-[#6B655B] font-mono-display tracking-wider">
                    {t.sub}
                  </div>
                </div>
              ))}
            </div>

            {/* Funnel + 7-day series */}
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div
                data-testid="ana-funnel"
                className="border border-[#DCD7C9] rounded-md p-5 bg-[#F4F1EA]"
              >
                <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                  Conversion funnel · all-time
                </div>
                <div className="mt-4 space-y-3">
                  {[
                    {
                      label: "Visit → Demo",
                      pct: analytics.funnel.visit_to_demo_pct,
                      from: analytics.visitors.total,
                      to: analytics.demo.total_users,
                    },
                    {
                      label: "Demo → Payment",
                      pct: analytics.funnel.demo_to_intent_pct,
                      from: analytics.demo.total_users,
                      to: analytics.payments.intents,
                    },
                    {
                      label: "Visit → Verified revenue",
                      pct: analytics.funnel.visit_to_verified_pct,
                      from: analytics.visitors.total,
                      to: analytics.payments.verified,
                    },
                  ].map((r) => (
                    <div key={r.label}>
                      <div className="flex justify-between text-[12px] font-mono-display uppercase tracking-[0.16em] text-[#1C1A17]/70">
                        <span>{r.label}</span>
                        <span>
                          {r.to} / {r.from} ·{" "}
                          <span className="text-[#0A1E3F] font-medium">
                            {r.pct}%
                          </span>
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 bg-[#DCD7C9] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#D4A53A]"
                          style={{ width: `${Math.min(100, r.pct)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                data-testid="ana-daily"
                className="border border-[#DCD7C9] rounded-md p-5 bg-[#F4F1EA]"
              >
                <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                  Daily unique visitors · last 7 days
                </div>
                <div className="mt-5 flex items-end justify-between gap-2 h-32">
                  {analytics.visitors.daily_series.map((d) => {
                    const max = Math.max(
                      1,
                      ...analytics.visitors.daily_series.map((x) => x.visitors)
                    );
                    const h = Math.max(4, (d.visitors / max) * 100);
                    const dayLabel = d.date.slice(-2);
                    return (
                      <div
                        key={d.date}
                        className="flex-1 flex flex-col items-center justify-end h-full"
                      >
                        <div className="text-[10px] font-mono-display text-[#0A1E3F] mb-1">
                          {d.visitors}
                        </div>
                        <div
                          className="w-full bg-[#0A1E3F] rounded-t"
                          style={{ height: `${h}%`, minHeight: "4px" }}
                        />
                        <div className="text-[10px] font-mono-display text-[#6B655B] mt-1">
                          {dayLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 text-[11px] text-[#6B655B] font-mono-display tracking-wider">
                  {analytics.visitors.pageviews_total} total pageviews
                </div>
              </div>
            </div>

            {/* Worksheet (lead-magnet) funnel */}
            {analytics.worksheet && (
              <div
                data-testid="ana-worksheet-funnel"
                className="mt-3 border border-[#DCD7C9] rounded-md p-5 bg-[#0A1E3F] text-[#F4F1EA]"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#D4A53A]">
                    Worksheet funnel · is the lead magnet earning its keep?
                  </div>
                  <div className="font-mono-display text-[10px] uppercase tracking-[0.16em] text-[#F4F1EA]/55">
                    {analytics.worksheet.download_to_pro_pct}% download → Pro
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-0 items-stretch">
                  {[
                    {
                      label: "Worksheet downloads",
                      value: analytics.worksheet.downloads,
                      sub: "leads captured",
                      testid: "ws-stat-downloads",
                    },
                    {
                      label: "Follow-up sent",
                      value: analytics.worksheet.followed_up,
                      sub: `${analytics.worksheet.downloads ? Math.round((analytics.worksheet.followed_up / analytics.worksheet.downloads) * 100) : 0}% of downloads`,
                      testid: "ws-stat-followed",
                    },
                    {
                      label: "Converted to Pro",
                      value: analytics.worksheet.converted,
                      sub: `${analytics.worksheet.followup_to_pro_pct}% of follow-ups`,
                      testid: "ws-stat-converted",
                    },
                  ].map((s, idx, arr) => (
                    <div
                      key={s.label}
                      data-testid={s.testid}
                      className={`flex flex-col justify-between px-5 py-3 ${idx < arr.length - 1 ? "border-r border-[#F4F1EA]/15" : ""}`}
                    >
                      <div>
                        <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#F4F1EA]/55">
                          {s.label}
                        </div>
                        <div className="font-serif text-4xl md:text-5xl tracking-tighter mt-2 text-[#F4F1EA]">
                          {s.value}
                        </div>
                      </div>
                      <div className="mt-1 text-[11px] text-[#F4F1EA]/55 font-mono-display tracking-wider">
                        {s.sub}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex items-center gap-3 text-[11px] font-mono-display uppercase tracking-[0.16em] text-[#F4F1EA]/60">
                  <span>{analytics.worksheet.downloads}</span>
                  <span className="flex-1 h-1 bg-[#F4F1EA]/15 rounded-full overflow-hidden">
                    <span
                      className="block h-full bg-[#D4A53A]"
                      style={{ width: `${Math.max(2, Math.min(100, analytics.worksheet.downloads ? (analytics.worksheet.followed_up / analytics.worksheet.downloads) * 100 : 0))}%` }}
                    />
                  </span>
                  <span>{analytics.worksheet.followed_up}</span>
                  <span className="flex-1 h-1 bg-[#F4F1EA]/15 rounded-full overflow-hidden">
                    <span
                      className="block h-full bg-[#D4A53A]"
                      style={{ width: `${Math.max(2, Math.min(100, analytics.worksheet.followed_up ? (analytics.worksheet.converted / analytics.worksheet.followed_up) * 100 : 0))}%` }}
                    />
                  </span>
                  <span>{analytics.worksheet.converted}</span>
                </div>
              </div>
            )}

            {/* Newsletter subscribers tile */}
            {analytics.newsletter && (
              <div
                data-testid="ana-newsletter-tile"
                className="mt-3 border border-[#DCD7C9] rounded-md p-5 bg-[#F4F1EA]"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                    Newsletter · footer signups
                  </div>
                  <div className="font-mono-display text-[10px] uppercase tracking-[0.16em] text-[#0A1E3F]">
                    +{analytics.newsletter.this_week} this week
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-0 items-stretch">
                  <div
                    data-testid="nl-stat-total"
                    className="flex flex-col px-5 py-3 border-r border-[#DCD7C9]"
                  >
                    <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                      Total subscribers
                    </div>
                    <div className="font-serif text-4xl md:text-5xl tracking-tighter mt-2 text-[#0A1E3F]">
                      {analytics.newsletter.total}
                    </div>
                  </div>
                  <div
                    data-testid="nl-stat-week"
                    className="flex flex-col px-5 py-3"
                  >
                    <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                      New this week
                    </div>
                    <div className="font-serif text-4xl md:text-5xl tracking-tighter mt-2 text-[#D4A53A]">
                      {analytics.newsletter.this_week}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Clarity-call clicks tile */}
            {analytics.clarity_call && (
              <div
                data-testid="ana-clarity-tile"
                className="mt-3 border border-[#DCD7C9] rounded-md p-5 bg-[#F4F1EA]"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                    Clarity call · CTA clicks
                  </div>
                  <div className="font-mono-display text-[10px] uppercase tracking-[0.16em] text-[#0A1E3F]">
                    +{analytics.clarity_call.clicks_this_week} this week
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-0 items-stretch">
                  <div
                    data-testid="cc-stat-total"
                    className="flex flex-col px-5 py-3 border-r border-[#DCD7C9]"
                  >
                    <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                      Total clicks
                    </div>
                    <div className="font-serif text-4xl md:text-5xl tracking-tighter mt-2 text-[#0A1E3F]">
                      {analytics.clarity_call.clicks}
                    </div>
                  </div>
                  <div
                    data-testid="cc-stat-week"
                    className="flex flex-col px-5 py-3"
                  >
                    <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                      This week
                    </div>
                    <div className="font-serif text-4xl md:text-5xl tracking-tighter mt-2 text-[#D4A53A]">
                      {analytics.clarity_call.clicks_this_week}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#0A1E3F]">
              Payment confirmations
            </h1>
            <p className="text-[#1C1A17]/70 mt-2">
              Verify UPI payments and activate customer access.
            </p>
          </div>
          <div className="flex gap-2 text-[12px] font-mono-display uppercase tracking-[0.16em]">
            {["all", "pending_verification", "verified", "rejected"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full border transition-all ${
                  filter === f
                    ? "bg-[#0A1E3F] text-[#D4A53A] border-[#0A1E3F]"
                    : "border-[#DCD7C9] text-[#1C1A17]/70 hover:border-[#0A1E3F]/40"
                }`}
              >
                {f.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
          {[
            { label: "Total intents", value: stats.total, testid: "stat-total" },
            { label: "Pending", value: stats.pending_verification || 0, testid: "stat-pending" },
            { label: "Verified", value: stats.verified || 0, testid: "stat-verified" },
            { label: "Verified revenue", value: fmtINR(stats.revenue), testid: "stat-revenue", wide: true },
          ].map((s) => (
            <div
              key={s.label}
              data-testid={s.testid}
              className="border border-[#DCD7C9] rounded-md p-5 bg-[#F4F1EA]"
            >
              <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                {s.label}
              </div>
              <div className="font-serif text-3xl md:text-4xl tracking-tighter mt-2 text-[#0A1E3F]">
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="mt-8 border border-[#DCD7C9] rounded-md overflow-hidden bg-[#F4F1EA]">
          {loading && items.length === 0 ? (
            <div className="p-10 text-center text-[#1C1A17]/60">
              <Loader2 size={20} className="animate-spin inline-block" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty" className="p-10 text-center text-[#1C1A17]/60">
              No payment confirmations {filter !== "all" ? `with status "${filter.replace("_", " ")}"` : "yet"}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-[#E6E2D6]/50">
                  <tr className="font-mono-display text-[10px] uppercase tracking-[0.16em] text-[#6B655B]">
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">UPI Reference</th>
                    <th className="px-4 py-3">Received</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <Row
                      key={row.id}
                      row={row}
                      token={token}
                      onChanged={() => load()}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {error && (
          <p data-testid="admin-error" className="mt-4 text-sm text-[#C65D47]">
            {error}
          </p>
        )}

        {/* Lead magnet subscribers */}
        <section data-testid="admin-leads" className="mt-16">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-[#0A1E3F]">
                Worksheet leads
              </h2>
              <p className="text-[#1C1A17]/70 mt-2 text-[15px]">
                Founders who downloaded the Brand Clarity Worksheet — your warmest list for follow-up.
              </p>
            </div>
            <div data-testid="leads-count" className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B]">
              {leads.length} subscriber{leads.length === 1 ? "" : "s"}
            </div>
          </div>

          <div className="mt-6 border border-[#DCD7C9] rounded-md overflow-hidden bg-[#F4F1EA]">
            {leads.length === 0 ? (
              <div data-testid="leads-empty" className="p-10 text-center text-[#1C1A17]/60">
                No worksheet downloads yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-[#E6E2D6]/50">
                    <tr className="font-mono-display text-[10px] uppercase tracking-[0.16em] text-[#6B655B]">
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">First seen</th>
                      <th className="px-4 py-3">Source</th>
                      <th className="px-4 py-3">Drip</th>
                      <th className="px-4 py-3 text-right">Follow up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l, i) => (
                      <tr key={l.email || i} data-testid={`lead-row-${i}`} className="border-t border-[#DCD7C9]">
                        <td className="px-4 py-3 font-serif text-[15px] text-[#0A1E3F]">{l.name}</td>
                        <td className="px-4 py-3 font-mono-display text-[12px] text-[#1C1A17]/80">
                          <a href={`mailto:${l.email}`} className="hover:text-[#0A1E3F] underline-offset-2 hover:underline">
                            {l.email}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-[13px] text-[#1C1A17]/65">{fmtDate(l.first_seen || l.created_at)}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono-display text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5 border border-[#DCD7C9] text-[#6B655B]">
                            {l.source || "worksheet"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {l.drip_1_sent_at ? (
                            <span
                              data-testid={`drip-sent-${i}`}
                              title={`Sent ${fmtDate(l.drip_1_sent_at)}`}
                              className="inline-flex items-center gap-1.5 font-mono-display text-[10px] uppercase tracking-[0.16em] rounded-full px-2.5 py-1 border bg-[#0A1E3F] text-[#D4A53A] border-[#0A1E3F]"
                            >
                              <CheckCircle2 size={11} /> Followed-up
                            </span>
                          ) : (
                            <span
                              data-testid={`drip-pending-${i}`}
                              className="inline-flex items-center gap-1.5 font-mono-display text-[10px] uppercase tracking-[0.16em] rounded-full px-2.5 py-1 border bg-[#DCD7C9] text-[#0A1E3F] border-[#0A1E3F]/15"
                            >
                              <Clock size={11} /> In queue
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <a
                            href={`mailto:${l.email}?subject=${encodeURIComponent("Following up on your Brand Clarity Worksheet")}&body=${encodeURIComponent(`Hi ${l.name?.split(" ")[0] || "there"},\n\nNoticed you grabbed the Brand Clarity Worksheet — wanted to check in.\n\nIf you got stuck on any of the seven questions, we'd be happy to think through it with you. Or if you're ready to turn your answers into a real brand story, the AI generator at storyassembly.in is free for three goes a day.\n\n— The Story Assembly`)}`}
                            data-testid={`lead-followup-${i}`}
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 bg-[#0A1E3F] text-[#D4A53A] text-[11px] font-mono-display uppercase tracking-[0.16em] hover:bg-[#0A1E3F]/90"
                          >
                            Email
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminPage;
